import {
  createContext,
  Fragment,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import type { Call as VoiceCall, Device as VoiceDevice } from '@twilio/voice-sdk';
import { When } from 'react-if';
import { toast } from 'react-toastify';
import {
  CloseButton,
  Popover,
  PopoverButton,
  PopoverPanel,
} from '@headlessui/react';
import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ComputerDesktopIcon,
  DevicePhoneMobileIcon,
  MicrophoneIcon,
  PhoneIcon,
  PhoneXMarkIcon,
  SpeakerWaveIcon,
} from '@heroicons/react/20/solid';
import { PhoneArrowUpRightIcon } from '@heroicons/react/24/outline';
import {
  ActivityType,
  CALL_LOW_BALANCE_MINUTES,
  CALL_OVERDRAW_GRACE_MINUTES,
  CallMode,
  CallOutcome,
  CallStatus,
  CallTranscriptStatus,
  DemoCallScenario,
  DemoCallScenarioLabels,
  displayPhoneNumber,
  ICallerIdsResponse,
  ICallFollowUps,
  ICallTranscriptSegment,
  IContactPerson,
  ISingleContactResponse,
  OrganisationFeatureFlags,
  callPricePerMinuteCents,
  currencyFormatter,
} from 'shared';

import { Button } from '../../../../../common/Atoms/Button';
import { Modal } from '../../../../../common/Atoms/Modal';
import { Select } from '../../../../../common/Atoms/Select';
import { ToolbarButton } from '../../../../../common/Atoms/ToolbarButton';
import { classNames } from '../../../../../common/lib/classNames';
import { env } from '../../../../../common/lib/env';
import { useContact } from '../../../../hooks/useContact';
import { useGetSelectedOrganisation } from '../../../../hooks/useGetSelectedOrganisation';
import { useSettings } from '../../../../hooks/useSettings';
import { useUser } from '../../../../hooks/useUser';
import {
  useCancelCallMutation,
  useFetchCallStatusQuery,
  useFetchCallerIdsQuery,
  useFetchWalletBalanceQuery,
  usePlaceCallMutation,
} from '../../../../services/api/contactsApi/contact';
import { CallPricingLink } from '../../../Calls/CallPricing';
import { LogActivityModal } from '../LogActivityModal';
import { formatDuration, TranscriptBubbles } from '../../../Molecules/CallTranscript';

import { CallRecording } from './CallRecording';

export type CallField = `mobile` | `default`;

export interface ICallTarget {
  person: IContactPerson;
  field: CallField;
  /** Number as shown on the page, e.g. "+61 412 345 678" */
  display: string;
}

const endedStatuses: CallStatus[] = [`completed`, `busy`, `no-answer`, `failed`, `canceled`];

const endedLabels: Partial<Record<CallStatus, string>> = {
  busy: `Busy`,
  'no-answer': `No answer`,
  failed: `Call failed`,
  canceled: `Cancelled`,
};

const settledTranscriptStatuses: CallTranscriptStatus[] = [`completed`, `failed`, `none`];

/** How long after hangup to keep waiting for the transcript before letting the user log without it */
const TRANSCRIPT_WAIT_MS = 120_000;

/** How long status polls may fail with a 5xx or network error (a deploy, a cold start) before we stop tracking the call */
const STATUS_ERROR_GRACE_MS = 120_000;

export function callPersonName(person: IContactPerson) {
  return `${person.firstName || ``} ${person.lastName || ``}`.trim() || `No name`;
}

/** Loose check only. The backend does the real E.164 parse and rejects anything it cannot dial */
export function hasDiallableDigits(countryCode?: string, number?: string) {
  return `${countryCode || ``}${number || ``}`.replace(/\D/g, ``).length >= 6;
}

export function callTargetFor(person: IContactPerson, field: CallField): ICallTarget | null {
  const countryCode = field === `mobile` ? person.mobileCountryCode : person.defaultCountryCode;
  const number = field === `mobile` ? person.mobileNumber : person.defaultNumber;

  if (!person.id || !hasDiallableDigits(countryCode, number)) {
    return null;
  }

  return { person, field, display: displayPhoneNumber(countryCode, number) || `` };
}

export function callTargets(contact: ISingleContactResponse): ICallTarget[] {
  const people = [contact.primaryContactPerson, ...(contact.contactPersons || [])].filter(Boolean);

  return people.flatMap(person => [callTargetFor(person, `mobile`), callTargetFor(person, `default`)].filter((t): t is ICallTarget => !!t));
}

interface IActiveCall {
  callId: string;
  mode: CallMode;
  target: ICallTarget;
  personName: string;
  to: string;
  /** When the call was placed. Stands in for the last good status poll until the first one lands */
  placedAt: number;
  /** Set once Twilio reports the call ended, so we can give up on the transcript */
  endedAt?: number;
  /** Browser mode: what the Voice SDK says, which is ahead of the polled Twilio status */
  browserState?: `connecting` | `connected` | `ended`;
  /** Browser mode: the SDK could not start the call (no microphone, bad token). The row times out server side */
  error?: string;
  /** The org auto transcribes calls, so it records from answer */
  transcribing: boolean;
}

/** Picked from the call panel, waiting on the user to press Start call */
interface IPendingCall {
  target: ICallTarget;
  mode: CallMode;
  fromNumber?: string;
  /** Demo stack only: the canned conversation Start call plays instead of dialling */
  demoScenario?: DemoCallScenario;
}

/** Browser mode: device ids picked on the pre call screen. Undefined means the browser default */
interface IAudioDevices {
  input?: string;
  output?: string;
}

interface ILogDraft {
  contactCallId: string;
  callOutcome: CallOutcome | null;
  note: string;
  followUps: ICallFollowUps | null;
  transcript: ICallTranscriptSegment[] | null;
}

type CallPhase = `ringing` | `connected` | `ended` | `transcribing` | `done` | `error`;

/** Twilio Voice SDK errors worth a plain English message. Anything else shows the SDK's own text */
function browserCallErrorMessage(err: unknown) {
  const code = (err as { code?: number })?.code;

  if (code === 31208 || (err as { name?: string })?.name === `NotAllowedError`) {
    return `Your browser blocked the microphone. Allow it for this site, or ring your phone instead.`;
  }

  if (code === 31402 || (err as { name?: string })?.name === `NotFoundError`) {
    return `No microphone found. Plug one in, or ring your phone instead.`;
  }

  return (err as { message?: string })?.message || `The browser could not start the call`;
}

/**
 * Twilio sends no early media to a browser leg while the contact's phone rings, so the caller hears silence until
 * pickup. Play a local ringback tone (AU/NZ cadence, 400 + 450 Hz) until the first bytes arrive from Twilio.
 */
class Ringback {
  private context: AudioContext | null = null;

  private timer: ReturnType<typeof setInterval> | null = null;

  start() {
    if (this.context || typeof AudioContext === `undefined`) return;

    const context = new AudioContext();
    const gain = context.createGain();

    gain.gain.value = 0;
    gain.connect(context.destination);

    [400, 450].forEach(frequency => {
      const oscillator = context.createOscillator();

      oscillator.frequency.value = frequency;
      oscillator.connect(gain);
      oscillator.start();
    });

    // 0.4s on, 0.2s off, 0.4s on, 2s off
    const burst = () => {
      const now = context.currentTime;

      [0, 0.6].forEach(offset => {
        gain.gain.setValueAtTime(0.15, now + offset);
        gain.gain.setValueAtTime(0, now + offset + 0.4);
      });
    };

    burst();
    this.timer = setInterval(burst, 3000);
    this.context = context;
  }

  stop() {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
    this.context?.close().catch(() => undefined);
    this.context = null;
  }
}

/** Spike only: Voice SDK media diagnostics in the console outside production, to chase one way audio reports */
const callDebug = import.meta.env.VITE_ENVIRONMENT !== `production`;

function attachCallDiagnostics(voiceCall: VoiceCall) {
  const log = (message: string, data?: unknown) => console.info(`[click-to-call] ${message}`, data ?? ``);

  // Remote audio element the SDK plays the far end through. paused or muted here means playback, not network
  voiceCall.on(`audio`, (remoteAudio: HTMLAudioElement) => {
    const snapshot = () => ({
      paused: remoteAudio.paused,
      muted: remoteAudio.muted,
      volume: remoteAudio.volume,
      readyState: remoteAudio.readyState,
      sinkId: (remoteAudio as HTMLAudioElement & { sinkId?: string }).sinkId,
      tracks: (remoteAudio.srcObject as MediaStream | null)?.getAudioTracks().map(track => ({ enabled: track.enabled, muted: track.muted, readyState: track.readyState })),
    });

    log(`remote audio element`, snapshot());
    setTimeout(() => log(`remote audio element after 5s`, snapshot()), 5000);
  });

  voiceCall.on(`warning`, (name: string, data: unknown) => log(`warning ${name}`, data));
  voiceCall.on(`warning-cleared`, (name: string) => log(`warning cleared ${name}`));

  // One sample a second. bytesReceived flat at zero means Twilio media never reached the browser
  let samples = 0;

  voiceCall.on(`sample`, (sample: { bytesReceived: number; packetsReceived: number; audioOutputLevel: number; audioInputLevel: number; bytesSent: number }) => {
    samples += 1;

    if (samples % 5 === 1) {
      log(`sample ${samples}`, {
        bytesReceived: sample.bytesReceived,
        packetsReceived: sample.packetsReceived,
        bytesSent: sample.bytesSent,
        audioOutputLevel: sample.audioOutputLevel,
        audioInputLevel: sample.audioInputLevel,
      });
    }
  });
}

interface IClickToCall {
  enabled: boolean;
  browserEnabled: boolean;
  call: (target: ICallTarget, mode?: CallMode, fromNumber?: string) => Promise<void>;
  /** Numbers the org can call from and the recommended one per contact number. Undefined while loading */
  callerIds: ICallerIdsResponse | undefined;
  busy: boolean;
  targets: ICallTarget[];
}

const ClickToCallContext = createContext<IClickToCall | null>(null);

/** Call state for the contact page. One instance per page so the header and people card share one call and one modal */
export function useClickToCall(): IClickToCall {
  const context = useContext(ClickToCallContext);

  if (!context) {
    throw new Error(`useClickToCall needs a ClickToCallProvider above it`);
  }

  return context;
}

/** Wrap the contact page once. Renders the call status and Record Activity modals for whichever surface started the call */
export function ClickToCallProvider({ children }: { children: ReactNode }) {
  const { modal, ...state } = useClickToCallState();

  return (
    <ClickToCallContext.Provider value={ state }>
      { children }
      { modal }
    </ClickToCallContext.Provider>
  );
}

/**
 * Click to call in two modes. Bridge: Twilio rings the user's own phone, then dials the contact. Browser: the Twilio
 * Voice SDK places the call from the page with mute and hang up here. Either way a status modal follows the call and
 * shows the transcript, then the Record Activity modal opens pre filled with the outcome, duration and AI summary.
 */
function useClickToCallState() {
  const { data: contact } = useContact();
  const organisation = useGetSelectedOrganisation();
  const { data: user } = useUser();
  const [placeCall, { isLoading: placing }] = usePlaceCallMutation();
  const { data: callerIds } = useFetchCallerIdsQuery(
    { organisationId: contact?.organisationId || ``, contactId: contact?.id || 0 },
    { skip: !contact || !organisation?.featureFlags?.includes(OrganisationFeatureFlags.CLICK_TO_CALL) },
  );
  const [cancelCall] = useCancelCallMutation();
  const [pending, setPending] = useState<IPendingCall | null>(null);
  const [active, setActive] = useState<IActiveCall | null>(null);
  const [draft, setDraft] = useState<ILogDraft | null>(null);
  const [muted, setMuted] = useState(false);
  const [devices, setDevices] = useState<IAudioDevices>({});
  const deviceRef = useRef<VoiceDevice | null>(null);
  const voiceCallRef = useRef<VoiceCall | null>(null);
  /** The browser call the page still wants. Closing clears it so a connect still in flight drops its call */
  const liveCallIdRef = useRef<string | null>(null);
  const ringbackRef = useRef(new Ringback());
  const enabled = !!organisation?.featureFlags?.includes(OrganisationFeatureFlags.CLICK_TO_CALL);
  const browserEnabled = !!user?.browserCallingEnabled;

  const { data: status, error: statusError, fulfilledTimeStamp } = useFetchCallStatusQuery(
    { organisationId: contact?.organisationId || ``, contactId: contact?.id || 0, callId: active?.callId || `` },
    { skip: !active || !contact || !!active.error, pollingInterval: 3000 },
  );

  const ended = !!active && !!status && endedStatuses.includes(status.status);
  const transcriptSettled = !!status && settledTranscriptStatuses.includes(status.transcriptStatus);
  const waitedLongEnough = !!active?.endedAt && Date.now() - active.endedAt > TRANSCRIPT_WAIT_MS;
  const canLog = ended && (transcriptSettled || waitedLongEnough);

  useEffect(() => {
    if (!active) return;

    if (statusError) {
      const httpStatus = (statusError as { status?: number }).status;
      const transient = !httpStatus || httpStatus >= 500;
      const lastGoodPoll = Math.max(fulfilledTimeStamp || 0, active.placedAt);

      // Keep polling through a deploy or a blip. A 4xx is a real answer, so that gives up straight away
      if (transient && Date.now() - lastGoodPoll < STATUS_ERROR_GRACE_MS) return;

      // A live browser call keeps the modal so hang up and mute still work. Its disconnect clears the ref and
      // changes active, which runs this again
      if (voiceCallRef.current) return;

      toast.error(`Lost track of the call. Log it manually once you hang up.`);
      setActive(null);

      return;
    }

    if (ended && !active.endedAt) {
      setActive({ ...active, endedAt: Date.now() });
    }
  }, [status, statusError, fulfilledTimeStamp, active, ended]);

  function phase(): CallPhase {
    if (active?.error) return `error`;

    if (!ended) {
      // A demo browser call has no Voice SDK state, so it follows the polled status like bridge
      const connected = active?.browserState ? active.browserState === `connected` : status?.status === `in-progress`;

      return connected ? `connected` : `ringing`;
    }

    if (status?.status !== `completed`) return `ended`;

    return canLog ? `done` : `transcribing`;
  }

  function teardownBrowserCall() {
    ringbackRef.current.stop();
    voiceCallRef.current = null;
    deviceRef.current?.destroy();
    deviceRef.current = null;
    setMuted(false);
  }

  // Drop the Voice SDK device if the page unmounts mid call. Twilio ends the call when the media stream goes.
  // Clearing the ref also stops a connect still loading the SDK from dialling after the page has gone
  useEffect(() => () => {
    liveCallIdRef.current = null;
    teardownBrowserCall();
  }, []);

  async function connectBrowserCall(callId: string, token: string) {
    const { Device, Call } = await import(`@twilio/voice-sdk`);

    if (liveCallIdRef.current !== callId) return;

    const device = new Device(token, { codecPreferences: [Call.Codec.Opus, Call.Codec.PCMU], logLevel: callDebug ? `info` : `warn` });

    deviceRef.current = device;

    if (devices.input) {
      await device.audio?.setInputDevice(devices.input);
    }

    if (devices.output && device.audio?.isOutputSelectionSupported) {
      await device.audio.speakerDevices.set(devices.output);
    }

    const voiceCall = await device.connect({ params: { callId } });

    // Closed while connecting. The teardown already destroyed the device, this makes sure the call goes with it
    if (liveCallIdRef.current !== callId) {
      voiceCall.disconnect();

      return;
    }

    voiceCallRef.current = voiceCall;

    if (callDebug) {
      attachCallDiagnostics(voiceCall);
    }

    voiceCall.on(`accept`, () => {
      ringbackRef.current.start();
      setActive(current => (current?.callId === callId ? { ...current, browserState: `connected` } : current));
    });

    // One sample a second. The first bytes from Twilio mean the contact picked up (or Twilio is talking to us)
    voiceCall.on(`sample`, (sample: { bytesReceived: number }) => {
      if (sample.bytesReceived > 0) ringbackRef.current.stop();
    });

    voiceCall.on(`mute`, (isMuted: boolean) => setMuted(isMuted));

    voiceCall.on(`disconnect`, () => {
      setActive(current => (current?.callId === callId ? { ...current, browserState: `ended` } : current));
      teardownBrowserCall();
    });

    voiceCall.on(`error`, (err: unknown) => {
      if (voiceCall.status() === `open`) {
        // Mid call errors end the call through disconnect, the Twilio status poll records the outcome
        teardownBrowserCall();

        return;
      }

      abandonBrowserCall(callId, err);
    });
  }

  function hangUp() {
    voiceCallRef.current?.disconnect();
  }

  function toggleMute() {
    voiceCallRef.current?.mute(!voiceCallRef.current.isMuted());
  }

  /** Ends a browser call still running. Bridge calls run on the user's phone, so closing only stops following them */
  function closeCall() {
    // Not connected yet: fail the row now so a late voice webhook refuses to dial. No-op once Twilio has a CallSid
    if (active?.browserState === `connecting` && contact) {
      cancelCall({ organisationId: contact.organisationId, contactId: contact.id, callId: active.callId }).catch(() => undefined);
    }

    liveCallIdRef.current = null;
    voiceCallRef.current?.disconnect();
    teardownBrowserCall();
    setActive(null);
  }

  /** Browser mode never reached Twilio. Tell the backend now so the row does not sit queued until the timeout */
  function abandonBrowserCall(callId: string, err: unknown) {
    teardownBrowserCall();
    setActive(current => (current?.callId === callId ? { ...current, error: browserCallErrorMessage(err) } : current));

    if (contact) {
      cancelCall({ organisationId: contact.organisationId, contactId: contact.id, callId }).catch(() => undefined);
    }
  }

  function openLog() {
    if (!active || !status) return;

    const outcome = status.status === `completed` ? formatDuration(status.duration) : endedLabels[status.status];
    const header = `Called ${active.personName} on ${active.to} via Paidnice (${outcome})`;

    // The transcript stays on the call row and shows under the logged activity, the note only gets the summary
    setDraft({
      contactCallId: active.callId,
      callOutcome: status.outcome,
      note: status.summary ? `${header}\n\n${status.summary}` : header,
      followUps: status.followUps,
      transcript: status.transcript,
    });

    setActive(null);
  }

  /** Opens the pre call screen. Nothing is dialled until the user presses Start call */
  async function call(target: ICallTarget, mode: CallMode = `bridge`, fromNumber?: string) {
    if (!contact || !target.person.id) return;

    // Demo calls never ring the user's phone
    if (mode === `bridge` && !user?.phoneNumber && !env.paidniceDemo) {
      toast.error(`Add your phone number under My Account first. We ring you, then connect the call.`);

      return;
    }

    setPending({ target, mode, fromNumber, ...(env.paidniceDemo ? { demoScenario: DemoCallScenario.PROMISE_NAMED } : {}) });
  }

  async function placeAndConnect(target: ICallTarget, mode: CallMode, fromNumber?: string, demoScenario?: DemoCallScenario) {
    if (!contact || !target.person.id) return;

    teardownBrowserCall();

    try {
      const result = await placeCall({
        organisationId: contact.organisationId,
        contactId: contact.id,
        personId: target.person.id,
        field: target.field,
        mode,
        fromNumber,
        demoScenario,
      }).unwrap();

      const next: IActiveCall = {
        callId: result.callId,
        mode,
        target,
        personName: callPersonName(target.person),
        to: result.to,
        placedAt: Date.now(),
        transcribing: result.transcribing,
        ...(mode === `browser` && result.token ? { browserState: `connecting` as const } : {}),
      };

      liveCallIdRef.current = result.callId;
      setActive(next);
      setPending(null);

      if (mode === `browser` && result.token) {
        try {
          await connectBrowserCall(result.callId, result.token);
        }
        catch (err) {
          abandonBrowserCall(result.callId, err);
        }
      }
    }
    catch (err) {
      const message = (err as { data?: { message?: string } })?.data?.message;

      toast.error(message || `Could not place the call`);
    }
  }

  const modal: ReactNode = contact ? (
    <>
      <CallStatusModal
        organisationId={ contact.organisationId }
        contactId={ contact.id }
        pending={ pending }
        placing={ placing }
        devices={ devices }
        onDevicesChange={ setDevices }
        onStart={ () => pending && placeAndConnect(pending.target, pending.mode, pending.fromNumber, pending.demoScenario) }
        onScenarioChange={ demoScenario => setPending(current => (current ? { ...current, demoScenario } : current)) }
        onCancelPending={ () => setPending(null) }
        active={ active }
        phase={ phase() }
        status={ status?.status }
        transcript={ status?.transcript || null }
        userPhone={ user?.phoneNumber || `` }
        muted={ muted }
        canLog={ canLog }
        onLog={ openLog }
        onHangUp={ hangUp }
        onToggleMute={ toggleMute }
        onClose={ closeCall }
        onRingMyPhone={ active?.target && user?.phoneNumber ? () => placeAndConnect(active.target, `bridge`) : undefined }
      />
      <LogActivityModal
        open={ draft !== null }
        setOpen={ open => !open && setDraft(null) }
        organisationId={ contact.organisationId }
        contactId={ contact.id }
        contactCallId={ draft?.contactCallId }
        initialType={ ActivityType.PHONE_CALL }
        initialCallOutcome={ draft?.callOutcome }
        initialNote={ draft?.note || `` }
        followUps={ draft?.followUps }
        transcript={ draft?.transcript }
        callerName={ user?.firstName }
      />
    </>
  ) : null;

  return {
    enabled,
    browserEnabled,
    call,
    callerIds,
    busy: placing || !!active || !!pending,
    targets: contact ? callTargets(contact) : [],
    modal,
  };
}

const steps: { phase: CallPhase; label: string }[] = [
  { phase: `ringing`, label: `Ringing` },
  { phase: `connected`, label: `Connected` },
  { phase: `transcribing`, label: `Transcript` },
];

function stepIndex(phase: CallPhase) {
  if (phase === `ringing` || phase === `error`) return 0;
  if (phase === `connected`) return 1;
  if (phase === `ended`) return 1;
  if (phase === `transcribing`) return 2;

  return 3;
}

/** Under the call status. Follows the org setting, read fresh for each panel */
function useRecordingNotice() {
  const { data: settings } = useSettings();

  return settings?.autoTranscribeCalls ?
    `Calls are recorded and transcribed automatically for this organisation.` :
    `Nothing is recorded until you click Start Transcribing. Only transcribed calls get a summary.`;
}

interface ICallStatusModalProps {
  organisationId: string;
  contactId: number;
  pending: IPendingCall | null;
  placing: boolean;
  devices: IAudioDevices;
  onDevicesChange: (devices: IAudioDevices) => void;
  onStart: () => void;
  onScenarioChange: (scenario: DemoCallScenario) => void;
  onCancelPending: () => void;
  active: IActiveCall | null;
  phase: CallPhase;
  status?: CallStatus;
  transcript: ICallTranscriptSegment[] | null;
  userPhone: string;
  muted: boolean;
  canLog: boolean;
  onLog: () => void;
  onHangUp: () => void;
  onToggleMute: () => void;
  onClose: () => void;
  /** Error state: retry the same number in bridge mode. Undefined when the user has no phone number saved */
  onRingMyPhone?: () => void;
}

/**
 * Starts on the pre call screen, then follows the call: ringing, connected, ended and transcribing. Closing a live
 * browser call asks first, then hangs up. A live bridge call cannot be closed, the user hangs up on their phone.
 */
function CallStatusModal({
  organisationId, contactId, pending, placing, devices, onDevicesChange, onStart, onScenarioChange, onCancelPending,
  active, phase, status, transcript, userPhone, muted, canLog, onLog, onHangUp, onToggleMute, onClose, onRingMyPhone,
}: ICallStatusModalProps) {
  const recordingNotice = useRecordingNotice();
  const [confirmingEnd, setConfirmingEnd] = useState(false);
  const [connectedAt, setConnectedAt] = useState<number | null>(null);
  const [, setTick] = useState(0);

  useEffect(() => {
    setConfirmingEnd(false);
    setConnectedAt(null);
  }, [active?.callId]);

  useEffect(() => {
    if (phase === `connected` && !connectedAt) {
      setConnectedAt(Date.now());
    }
  }, [phase, connectedAt]);

  // Re-render each second for the elapsed timer and the transcript wait
  useEffect(() => {
    if (!active || phase === `done`) return;

    const interval = setInterval(() => setTick(t => t + 1), 1000);

    return () => clearInterval(interval);
  }, [active, phase]);

  // Same Modal element as the live view below, so pressing Start swaps the content without a close and reopen
  if (!active) {
    return pending ? (
      <Modal
        open
        setOpen={ open => !open && !placing && onCancelPending() }
        width={ `lg` }
      >
        <PreCallPanel
          organisationId={ organisationId }
          pending={ pending }
          placing={ placing }
          userPhone={ userPhone }
          devices={ devices }
          onDevicesChange={ onDevicesChange }
          onStart={ onStart }
          onScenarioChange={ onScenarioChange }
          onCancel={ onCancelPending }
        />
      </Modal>
    ) : null;
  }

  const live = phase === `ringing` || phase === `connected`;
  const browser = active.mode === `browser`;

  // Closing a live bridge call would stop following it, and the Record Activity prompt would never show
  function requestClose() {
    if (live && browser) {
      setConfirmingEnd(true);
    }
    else if (!live) {
      onClose();
    }
  }
  const elapsed = connectedAt ? Math.floor((Date.now() - connectedAt) / 1000) : 0;
  const current = stepIndex(phase);

  const headline = {
    ringing: browser ? `Calling ${active.personName}` : `Ringing your phone`,
    connected: `Connected to ${active.personName}`,
    ended: endedLabels[status as CallStatus] || `Call ended`,
    transcribing: `Call ended`,
    done: `Call ended`,
    error: `Could not start the call`,
  }[phase];

  const detail = {
    ringing: env.paidniceDemo ? `Demo call to ${active.personName}. Nothing is dialled.` : browser ?
      `Dialling ${active.to} from your browser. Allow microphone access if asked.` :
      `Answer ${userPhone} and we'll dial ${active.personName} on ${active.to}.`,
    connected: env.paidniceDemo ? `Playing the demo conversation.` : browser ? `Talking from your browser. Hang up here when you're done.` : `Hang up on your phone when you're done.`,
    ended: `${active.personName} on ${active.to} didn't connect. You can still log the attempt.`,
    transcribing: `Transcribing the recording. This usually takes under a minute.`,
    done: transcript?.length ? `Transcript ready. Review it, then record the activity.` : `No transcript for this call. Record the activity with your own notes.`,
    error: active.error || ``,
  }[phase];

  return (
    <Modal
      open
      setOpen={ open => !open && requestClose() }
      width={ `lg` }
    >
      <div className={ `flex h-[30rem] flex-col` }>
        <div className={ `flex items-center gap-4` }>
          <span className={ `relative flex h-14 w-14 shrink-0 items-center justify-center` }>
            <When condition={ live }>
              <span className={ `absolute inline-flex h-full w-full animate-ping rounded-full bg-paidniceGreen-200 opacity-75` } />
            </When>
            <span className={ classNames(
              `relative inline-flex h-14 w-14 items-center justify-center rounded-full`,
              live ? `bg-paidniceGreen-100 text-paidniceGreen-700` : `bg-gray-100 text-gray-600`,
            ) }>
              <PhoneArrowUpRightIcon className={ `h-7 w-7` } />
            </span>
          </span>
          <div className={ `min-w-0` }>
            <h3 className={ `text-lg font-semibold text-gray-900` }>{ headline }</h3>
            <p className={ `text-sm text-gray-600` }>{ detail }</p>
          </div>
          <div className={ `ml-auto flex shrink-0 items-center gap-2` }>
            <When condition={ phase === `connected` }>
              <span className={ `rounded-md bg-paidniceGreen-50 px-2.5 py-1 font-mono text-lg tabular-nums text-paidniceGreen-700` }>
                { formatDuration(elapsed) }
              </span>
            </When>
            <When condition={ browser && live }>
              <button
                type={ `button` }
                onClick={ onToggleMute }
                title={ muted ? `Unmute` : `Mute` }
                aria-label={ muted ? `Unmute` : `Mute` }
                className={ classNames(
                  `flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border`,
                  muted ? `border-orange-300 bg-orange-50 text-orange-600` : `border-gray-300 bg-white text-gray-600 hover:bg-gray-50`,
                ) }
              >
                <MicrophoneIcon className={ classNames(`h-5 w-5`, muted && `opacity-50`) } />
              </button>
              <button
                type={ `button` }
                onClick={ onHangUp }
                title={ `Hang up` }
                aria-label={ `Hang up` }
                className={ `flex h-10 w-10 cursor-pointer items-center justify-center rounded-full bg-red-600 text-white hover:bg-red-700` }
              >
                <PhoneXMarkIcon className={ `h-5 w-5` } />
              </button>
            </When>
          </div>
        </div>

        <ol className={ `mt-5 flex items-center gap-2 text-xs` }>
          { steps.map((step, i) => (
            <li
              key={ step.phase }
              className={ `flex items-center gap-2` }
            >
              <span className={ classNames(
                `flex h-5 w-5 items-center justify-center rounded-full text-[0.65rem] font-semibold`,
                i < current && `bg-paidniceGreen-600 text-white`,
                i === current && `border-2 border-paidniceGreen-600 text-paidniceGreen-700`,
                i > current && `border border-gray-300 text-gray-400`,
              ) }>
                { i < current ? `✓` : i + 1 }
              </span>
              <span className={ i <= current ? `text-gray-900` : `text-gray-400` }>{ step.label }</span>
              <When condition={ i < steps.length - 1 }>
                <span className={ `h-px w-6 bg-gray-200` } />
              </When>
            </li>
          )) }
        </ol>

        <When condition={ phase === `connected` }>
          <CallRecording
            key={ active.callId }
            organisationId={ organisationId }
            contactId={ contactId }
            callId={ active.callId }
            autoTranscribing={ active.transcribing }
            elapsed={ elapsed }
          />
        </When>

        <div className={ `mt-4 flex min-h-0 flex-1 flex-col rounded-lg border border-gray-200 bg-gray-50` }>
          <When condition={ !!transcript?.length }>
            <div className={ `flex-1 overflow-y-auto p-3` }>
              <TranscriptBubbles
                segments={ transcript || [] }
                userLabel={ `You` }
                contactLabel={ active.personName }
              />
            </div>
          </When>
          <When condition={ !transcript?.length }>
            <div className={ `flex flex-1 flex-col items-center justify-center gap-2 p-6 text-center text-sm text-gray-500` }>
              <When condition={ phase === `transcribing` }>
                <span className={ `h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-paidniceGreen-600` } />
              </When>
              <span>
                { phase === `done` && `Nothing was transcribed.` }
                { phase === `error` && `Nothing to show. Close this, or try again by ringing your phone.` }
                { phase !== `done` && phase !== `error` && `The transcript will appear here once the call ends.` }
              </span>
            </div>
          </When>
        </div>

        <When condition={ confirmingEnd && live }>
          <div className={ `mt-4 flex items-center justify-between gap-4 rounded-lg bg-red-50 px-3 py-2` }>
            <p className={ `text-sm text-red-800` }>{ `Closing ends the call with ${active.personName}. End it now?` }</p>
            <div className={ `flex shrink-0 gap-2` }>
              <Button
                color={ `white` }
                onClick={ () => setConfirmingEnd(false) }
              >
                { `Keep calling` }
              </Button>
              <Button
                color={ `red` }
                onClick={ onClose }
              >
                { `End call` }
              </Button>
            </div>
          </div>
        </When>

        <div className={ classNames(`mt-4 flex items-center justify-between gap-4`, confirmingEnd && live && `hidden`) }>
          <p className={ `text-xs text-gray-500` }>{ recordingNotice }</p>
          <div className={ `flex shrink-0 gap-2` }>
            <When condition={ phase === `error` }>
              <Button
                color={ `white` }
                onClick={ onClose }
              >
                { `Close` }
              </Button>
              <When condition={ !!onRingMyPhone }>
                <Button
                  color={ `paidniceGreen` }
                  onClick={ onRingMyPhone }
                >
                  { `Ring my phone instead` }
                </Button>
              </When>
            </When>
            <When condition={ phase !== `error` }>
              <Button
                color={ `white` }
                onClick={ requestClose }
                disabled={ live && !browser }
              >
                { `Close` }
              </Button>
              <Button
                color={ `paidniceGreen` }
                onClick={ onLog }
                disabled={ !canLog }
              >
                { `Record activity` }
              </Button>
            </When>
          </div>
        </div>
      </div>
    </Modal>
  );
}

interface IPreCallPanelProps {
  organisationId: string;
  pending: IPendingCall;
  placing: boolean;
  userPhone: string;
  devices: IAudioDevices;
  onDevicesChange: (devices: IAudioDevices) => void;
  onStart: () => void;
  onScenarioChange: (scenario: DemoCallScenario) => void;
  onCancel: () => void;
}

const demoScenarioOptions = Object.values(DemoCallScenario).map(value => ({ label: DemoCallScenarioLabels[value], value }));

/** Who and how, plus a microphone and speaker check for browser calls. Nothing is dialled until Start call */
function PreCallPanel({ organisationId, pending, placing, userPhone, devices, onDevicesChange, onStart, onScenarioChange, onCancel }: IPreCallPanelProps) {
  const browser = pending.mode === `browser`;
  const demo = !!pending.demoScenario;
  const name = callPersonName(pending.target.person);
  const recordingNotice = useRecordingNotice();
  const { callerIds } = useClickToCall();
  const { data: user } = useUser();
  const { data: wallet } = useFetchWalletBalanceQuery({ organisationId }, { refetchOnMountOrArgChange: true });
  const destination = callerIds?.targets[`${pending.target.person.id}:${pending.target.field}`];
  const fromNumber = pending.fromNumber || destination?.recommended;
  const perMinuteCents = wallet && destination ? callPricePerMinuteCents({
    currency: wallet.currency,
    callerIdCountry: callerIds?.callerIds.find(option => option.number === fromNumber)?.isoCountry ?? null,
    destinationCountry: destination.isoCountry,
    destinationType: destination.numberType,
    // Demo calls are placed and priced as browser calls
    bridge: !browser && !demo,
    userPhoneCountry: user?.phoneCountry,
  }) : null;
  const noCredit = !!wallet && wallet.balanceCents <= 0;
  const minutesLeft = wallet && perMinuteCents ? Math.floor(wallet.balanceCents / perMinuteCents) : null;
  const money = (cents: number) => currencyFormatter(wallet?.currency || `USD`)(cents / 100);

  return (
    <div className={ `flex h-[30rem] flex-col` }>
      <div className={ `flex items-center gap-4` }>
        <span className={ `flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-paidniceGreen-100 text-paidniceGreen-700` }>
          <PhoneArrowUpRightIcon className={ `h-7 w-7` } />
        </span>
        <div className={ `min-w-0` }>
          <h3 className={ `text-lg font-semibold text-gray-900` }>{ `Call ${name}` }</h3>
          <p className={ `text-sm text-gray-600` }>
            { demo ? `Demo: nothing is dialled. Pick how the call goes and it plays out with a canned transcript.` : browser ?
              `${pending.target.display} from your browser${pending.fromNumber ? `, showing ${pending.fromNumber}` : ``}. Check your microphone and speaker, then start the call.` :
              `We'll ring ${userPhone}, then dial ${pending.target.display}${pending.fromNumber ? ` from ${pending.fromNumber}` : ``}.` }
          </p>
        </div>
      </div>

      <div className={ `mt-5 flex min-h-0 flex-1 flex-col rounded-lg border border-gray-200 bg-gray-50 p-4` }>
        { demo ? (
          <div className={ `m-auto w-full max-w-sm` }>
            <Select
              label={ `Scenario` }
              name={ `demoScenario` }
              options={ demoScenarioOptions }
              selected={ pending.demoScenario }
              onChange={ ({ value }) => onScenarioChange(value as DemoCallScenario) }
            />
          </div>
        ) : browser ? (
          <AudioCheck
            devices={ devices }
            onChange={ onDevicesChange }
          />
        ) : (
          <p className={ `m-auto text-center text-sm text-gray-500` }>{ `Answer your phone when it rings and we'll connect you.` }</p>
        ) }
      </div>

      { wallet ? (
        <div className={ `mt-4 flex flex-col gap-1 text-sm` }>
          <div className={ `flex items-center justify-between gap-4` }>
            <p className={ `text-gray-600` }>
              { perMinuteCents === null ?
                `Call credit: ${money(wallet.balanceCents)}` :
                `${money(perMinuteCents)} per minute, including transcription and summary. Call credit: ${money(wallet.balanceCents)}` }
            </p>
            <CallPricingLink currency={ wallet.currency } />
          </div>
          { noCredit ? (
            <p className={ `text-red-600` }>
              { wallet.canManage ? `Your call credit is used up. Add credit under Billing to make calls.` : `Your call credit is used up. Ask the subscription owner to add credit.` }
            </p>
          ) : minutesLeft !== null && minutesLeft < CALL_LOW_BALANCE_MINUTES ? (
            <p className={ `text-amber-700` }>
              { `Your credit covers about ${minutesLeft || `less than 1`} minute${minutesLeft === 1 ? `` : `s`}. The call may end automatically ${CALL_OVERDRAW_GRACE_MINUTES} minutes after it runs out.` }
            </p>
          ) : null }
        </div>
      ) : null }

      <div className={ `mt-4 flex items-center justify-between gap-4` }>
        <p className={ `text-xs text-gray-500` }>{ recordingNotice }</p>
        <div className={ `flex shrink-0 gap-2` }>
          <Button
            color={ `white` }
            onClick={ onCancel }
            disabled={ placing }
          >
            { `Cancel` }
          </Button>
          <Button
            color={ `paidniceGreen` }
            onClick={ onStart }
            loading={ placing }
            disabled={ noCredit || placing }
          >
            { `Start call` }
          </Button>
        </div>
      </div>
    </div>
  );
}

const outputSelectable = typeof HTMLMediaElement !== `undefined` && `setSinkId` in HTMLMediaElement.prototype;

/** Mic level meter and device pickers. Holds its own mic stream, released on unmount before the call takes the mic */
function AudioCheck({ devices, onChange }: { devices: IAudioDevices; onChange: (devices: IAudioDevices) => void }) {
  const [inputs, setInputs] = useState<MediaDeviceInfo[]>([]);
  const [outputs, setOutputs] = useState<MediaDeviceInfo[]>([]);
  const [level, setLevel] = useState(0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let stopped = false;
    let stream: MediaStream | null = null;
    let context: AudioContext | null = null;
    let frame = 0;

    // Labels are blank until the page has mic permission, so this runs again once getUserMedia succeeds
    async function listDevices() {
      const all = await navigator.mediaDevices.enumerateDevices();

      setInputs(all.filter(device => device.kind === `audioinput`));
      setOutputs(all.filter(device => device.kind === `audiooutput`));
    }

    async function start() {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: devices.input ? { deviceId: { exact: devices.input } } : true });
      }
      catch (err) {
        if (!stopped) setError(browserCallErrorMessage(err));

        return;
      }

      if (stopped) {
        stream.getTracks().forEach(track => track.stop());

        return;
      }

      setError(null);
      await listDevices();

      if (stopped) return;

      context = new AudioContext();

      const analyser = context.createAnalyser();
      const samples = new Uint8Array(analyser.fftSize);

      context.createMediaStreamSource(stream).connect(analyser);

      const tick = () => {
        analyser.getByteTimeDomainData(samples);
        // Peak distance from silence (128) as 0 to 1
        setLevel(samples.reduce((peak, sample) => Math.max(peak, Math.abs(sample - 128)), 0) / 128);
        frame = requestAnimationFrame(tick);
      };

      tick();
    }

    start();
    navigator.mediaDevices.addEventListener(`devicechange`, listDevices);

    return () => {
      stopped = true;
      cancelAnimationFrame(frame);
      stream?.getTracks().forEach(track => track.stop());
      context?.close().catch(() => undefined);
      navigator.mediaDevices.removeEventListener(`devicechange`, listDevices);
    };
  }, [devices.input]);

  async function testSpeaker() {
    const context = new AudioContext();
    const destination = context.createMediaStreamDestination();
    const oscillator = context.createOscillator();
    const audio = new Audio() as HTMLAudioElement & { setSinkId?: (id: string) => Promise<void> };

    oscillator.frequency.value = 440;
    oscillator.connect(destination);
    audio.srcObject = destination.stream;

    try {
      if (devices.output) await audio.setSinkId?.(devices.output);

      await audio.play();
      oscillator.start();
      oscillator.stop(context.currentTime + 0.8);
    }
    catch {
      toast.error(`Could not play the test sound`);
    }

    setTimeout(() => context.close().catch(() => undefined), 1000);
  }

  const deviceOptions = (list: MediaDeviceInfo[], fallback: string) => list.map((device, i) => ({
    value: device.deviceId,
    label: device.label || `${fallback} ${i + 1}`,
  }));

  return (
    <div className={ `flex flex-col gap-5` }>
      <div>
        <Select
          label={ `Microphone` }
          options={ deviceOptions(inputs, `Microphone`) }
          selected={ devices.input ?? inputs[0]?.deviceId }
          onChange={ ({ value }) => onChange({ ...devices, input: value }) }
          disabled={ !inputs.length }
        />
        <div className={ `mt-2 flex items-center gap-2` }>
          <MicrophoneIcon className={ `h-4 w-4 shrink-0 text-gray-400` } />
          <div className={ `h-2 flex-1 overflow-hidden rounded-full bg-gray-200` }>
            <div
              className={ `h-full rounded-full bg-paidniceGreen-500` }
              style={ { width: `${Math.min(100, level * 200)}%` } }
            />
          </div>
        </div>
        <p className={ classNames(`mt-1 text-xs`, error ? `text-red-600` : `text-gray-500`) }>
          { error || `Say something. The bar should move.` }
        </p>
      </div>

      <div className={ `flex items-end gap-2` }>
        <When condition={ outputSelectable && outputs.length > 0 }>
          <div className={ `flex-1` }>
            <Select
              label={ `Speaker` }
              options={ deviceOptions(outputs, `Speaker`) }
              selected={ devices.output ?? outputs[0]?.deviceId }
              onChange={ ({ value }) => onChange({ ...devices, output: value }) }
            />
          </div>
        </When>
        <Button
          color={ `white` }
          onClick={ testSpeaker }
        >
          <SpeakerWaveIcon className={ `mr-1 h-4 w-4` } />
          { `Test speaker` }
        </Button>
      </div>
    </div>
  );
}

// Bridge pays for two phone legs (the user's phone and the contact), browser pays for one plus a cheap client leg
const modeOptions: { mode: CallMode; label: string; icon: typeof PhoneIcon; hint: string }[] = [
  { mode: `browser`, label: `Browser`, icon: ComputerDesktopIcon, hint: `Standard cost` },
  { mode: `bridge`, label: `Ring my phone`, icon: DevicePhoneMobileIcon, hint: `2x cost` },
];

const panelClass = `z-50 mt-1 w-[340px] overflow-hidden rounded-lg border border-gray-200 bg-white text-sm shadow-lg focus:outline-none`;

const regionNames = new Intl.DisplayNames([`en`], { type: `region` });

function countryName(isoCountry: string) {
  try {
    return regionNames.of(isoCountry) || isoCountry;
  }
  catch {
    return isoCountry;
  }
}

function fieldLabel(target: ICallTarget) {
  return target.field === `mobile` ? `Mobile` : `Phone`;
}

type OnCall = (target: ICallTarget, mode: CallMode, fromNumber?: string) => void;

interface ICallTargetListProps {
  targets: ICallTarget[];
  onPick: (target: ICallTarget) => void;
}

/** Step one when the contact has more than one number: who to call */
function CallTargetList({ targets, onPick }: ICallTargetListProps) {
  return (
    <div>
      <div className={ `px-4 pb-1.5 pt-3 text-xs font-medium text-gray-500` }>{ `Who do you want to call?` }</div>
      <div className={ `px-1.5 pb-1.5` }>
        { targets.map(target => {
          const Icon = target.field === `mobile` ? DevicePhoneMobileIcon : PhoneIcon;

          return (
            <button
              key={ `${target.person.id}-${target.field}` }
              type={ `button` }
              onClick={ () => onPick(target) }
              className={ `flex w-full cursor-pointer items-center gap-3 rounded-lg p-2.5 text-left hover:bg-paidniceGreen-50 focus:bg-paidniceGreen-50 focus:outline-none` }
            >
              <span className={ `flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-paidniceGreen-50 text-paidniceGreen-600` }>
                <Icon className={ `h-4 w-4` } />
              </span>
              <span className={ `flex min-w-0 flex-1 flex-col` }>
                <span className={ `truncate font-medium text-gray-900` }>{ `${callPersonName(target.person)} · ${fieldLabel(target)}` }</span>
                <span className={ `text-[13px] tabular-nums text-gray-500` }>{ target.display }</span>
              </span>
              <ChevronRightIcon className={ `h-4 w-4 shrink-0 text-gray-400` } />
            </button>
          );
        }) }
      </div>
    </div>
  );
}

interface ICallTargetDetailProps {
  target: ICallTarget;
  browserEnabled: boolean;
  onCall: OnCall;
  /** Back to the number list. Missing when the panel only has one number */
  onBack?: () => void;
}

/**
 * Step two: which of the org's numbers to call from and how, for one contact number. The number in the destination's
 * country is preselected and marked recommended, any other country is flagged as international
 */
function CallTargetDetail({ target, browserEnabled, onCall, onBack }: ICallTargetDetailProps) {
  const { callerIds } = useClickToCall();
  const destination = callerIds?.targets[`${target.person.id}:${target.field}`];
  const options = callerIds?.callerIds || [];
  const modes = browserEnabled ? modeOptions : modeOptions.filter(option => option.mode === `bridge`);
  const [from, setFrom] = useState<string | undefined>(undefined);
  const [mode, setMode] = useState<CallMode>(modes[0].mode);
  const selected = from || destination?.recommended || options[0]?.number;
  const selectedOption = options.find(option => option.number === selected);
  const international = !!destination?.isoCountry && !!selectedOption && selectedOption.isoCountry !== destination.isoCountry;

  const header = (
    <>
      <span className={ `font-medium text-gray-900` }>{ onBack ? fieldLabel(target) : `${callPersonName(target.person)} · ${fieldLabel(target)}` }</span>
      <span className={ `truncate text-[13px] tabular-nums text-gray-500` }>{ target.display }</span>
    </>
  );

  return (
    <div>
      { onBack ? (
        <button
          type={ `button` }
          onClick={ onBack }
          className={ `flex w-full cursor-pointer items-center gap-2 border-b border-gray-100 px-4 py-3 text-left hover:bg-gray-50 focus:outline-none` }
        >
          <ChevronLeftIcon className={ `h-4 w-4 shrink-0 text-gray-500` } />
          { header }
        </button>
      ) : (
        <div className={ `flex items-center gap-2 border-b border-gray-100 px-4 py-3` }>{ header }</div>
      ) }

      <When condition={ !!callerIds && !options.length }>
        <p className={ `px-4 py-3 text-xs text-gray-500` }>{ `Buy a phone number on the Calls page to start calling.` }</p>
      </When>

      <When condition={ options.length > 0 }>
        <div className={ `flex flex-col gap-1.5 px-4 pt-3` }>
          <div className={ `text-xs font-medium text-gray-500` }>{ `Caller ID` }</div>
          <div
            role={ `radiogroup` }
            aria-label={ `Caller ID` }
            className={ `max-h-48 divide-y divide-gray-100 overflow-y-auto rounded-lg border border-gray-200` }
          >
            { options.map(option => {
              const checked = option.number === selected;

              return (
                <button
                  key={ option.number }
                  type={ `button` }
                  role={ `radio` }
                  aria-checked={ checked }
                  onClick={ () => setFrom(option.number) }
                  className={ classNames(
                    `flex w-full cursor-pointer items-center gap-2.5 px-3 py-2 text-left focus:outline-none`,
                    checked ? `bg-paidniceGreen-50` : `bg-white hover:bg-gray-50`,
                  ) }
                >
                  <span className={ classNames(
                    `flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full border`,
                    checked ? `border-paidniceGreen-600` : `border-gray-300`,
                  ) }
                  >
                    <When condition={ checked }>
                      <span className={ `h-1.5 w-1.5 rounded-full bg-paidniceGreen-600` } />
                    </When>
                  </span>
                  <span className={ `text-[13px] tabular-nums text-gray-900` }>{ option.number }</span>
                  <span className={ `text-[11px] font-semibold tracking-wide text-gray-500` }>{ option.isoCountry }</span>
                  { option.number === destination?.recommended ? (
                    <span className={ `ml-auto text-xs text-paidniceGreen-700` }>{ `Recommended` }</span>
                  ) : option.shared && (
                    <span className={ `ml-auto text-xs text-gray-400` }>{ `Shared` }</span>
                  ) }
                </button>
              );
            }) }
          </div>
          <When condition={ international }>
            <div className={ `flex items-center gap-1.5 text-xs text-amber-700` }>
              <span className={ `h-1.5 w-1.5 rounded-full bg-amber-500` } />
              { `International rate to ${countryName(destination?.isoCountry || ``)}` }
            </div>
          </When>
        </div>

        <div className={ `flex flex-col gap-1.5 px-4 pt-3.5` }>
          <div className={ `text-xs font-medium text-gray-500` }>{ `Call using` }</div>
          <div
            role={ `radiogroup` }
            aria-label={ `Call using` }
            className={ classNames(`grid gap-2`, modes.length > 1 ? `grid-cols-2` : `grid-cols-1`) }
          >
            { modes.map(option => {
              const checked = option.mode === mode;

              return (
                <button
                  key={ option.mode }
                  type={ `button` }
                  role={ `radio` }
                  aria-checked={ checked }
                  onClick={ () => setMode(option.mode) }
                  className={ classNames(
                    `flex cursor-pointer flex-col items-start gap-1 rounded-lg border px-3 py-2.5 text-left focus:outline-none`,
                    checked ? `border-paidniceGreen-600 bg-paidniceGreen-50 ring-1 ring-paidniceGreen-600` : `border-gray-200 bg-white hover:bg-gray-50`,
                  ) }
                >
                  <option.icon className={ `h-[18px] w-[18px] text-paidniceGreen-600` } />
                  <span className={ `text-[13px] font-medium text-gray-900` }>{ option.label }</span>
                  <span className={ `text-xs text-gray-500` }>{ option.hint }</span>
                </button>
              );
            }) }
          </div>
        </div>

        <div className={ `p-4` }>
          <CloseButton
            type={ `button` }
            onClick={ () => onCall(target, mode, selected) }
            className={ `flex h-[38px] w-full cursor-pointer items-center justify-center gap-2 rounded-lg bg-paidniceGreen-600 font-medium text-white hover:bg-paidniceGreen-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-paidniceGreen-600 focus-visible:ring-offset-2` }
          >
            <PhoneIcon className={ `h-4 w-4` } />
            { `Call ${target.display}` }
          </CloseButton>
        </div>
      </When>
    </div>
  );
}

interface ICallPanelProps {
  targets: ICallTarget[];
  browserEnabled: boolean;
  onCall: OnCall;
  anchor: `bottom start` | `bottom end`;
}

/** Two steps: pick a number, then caller ID and method. A single number skips straight to the second step */
function CallPanelSteps({ targets, browserEnabled, onCall }: Omit<ICallPanelProps, `anchor`>) {
  const [picked, setPicked] = useState<ICallTarget | null>(null);
  const target = targets.length === 1 ? targets[0] : picked;

  if (!target) {
    return (
      <CallTargetList
        targets={ targets }
        onPick={ setPicked }
      />
    );
  }

  return (
    <CallTargetDetail
      target={ target }
      browserEnabled={ browserEnabled }
      onCall={ onCall }
      onBack={ targets.length > 1 ? () => setPicked(null) : undefined }
    />
  );
}

/** Panel shared by the toolbar button and the inline icons. A popover, not a menu, so picking options keeps it open */
function CallPanel({ anchor, ...props }: ICallPanelProps) {
  return (
    <PopoverPanel
      anchor={ anchor }
      className={ panelClass }
    >
      <CallPanelSteps { ...props } />
    </PopoverPanel>
  );
}

interface ICallIconButtonProps {
  target: ICallTarget;
  browserEnabled: boolean;
  onCall: OnCall;
  disabled?: boolean;
  className?: string;
}

/** Small phone icon beside a number. Opens the call panel so the caller ID is always visible before dialling */
export function CallIconButton({ target, browserEnabled, onCall, disabled, className }: ICallIconButtonProps) {
  const title = `Call ${target.display}`;

  return (
    <Popover as={ `span` }>
      <PopoverButton
        type={ `button` }
        disabled={ disabled }
        title={ title }
        aria-label={ title }
        className={ classNames(
          `cursor-pointer rounded p-0.5 text-paidniceGreen-600 hover:bg-paidniceGreen-50 disabled:cursor-not-allowed disabled:opacity-50`,
          className,
        ) }
      >
        <PhoneIcon className={ `h-3.5 w-3.5` } />
      </PopoverButton>
      <CallPanel
        targets={ [target] }
        browserEnabled={ browserEnabled }
        onCall={ onCall }
        anchor={ `bottom start` }
      />
    </Popover>
  );
}

interface ICallToolbarButtonProps {
  targets: ICallTarget[];
  browserEnabled: boolean;
  onCall: OnCall;
  disabled?: boolean;
}

/** Page toolbar "Call" button. Always opens the panel so the user sees which number the call comes from */
export function CallToolbarButton({ targets, browserEnabled, onCall, disabled }: ICallToolbarButtonProps) {
  if (!targets.length) return null;

  return (
    <Popover>
      <PopoverButton
        as={ ToolbarButton }
        primary
        icon={ PhoneIcon }
        disabled={ disabled }
      >
        { `Call` }
        <ChevronDownIcon className={ `-mr-1 h-4 w-4 text-white/80` } />
      </PopoverButton>
      <CallPanel
        targets={ targets }
        browserEnabled={ browserEnabled }
        onCall={ onCall }
        anchor={ `bottom start` }
      />
    </Popover>
  );
}
